import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Search, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });

export function GoogleReviewsDialog({ open, onClose, pageId, page, onChanged }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const linked = page?.content?.google_reviews;

  const search = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/pages/${pageId}/google/search`, { params: { q }, headers: authHeaders() });
      setResults(r.data.places);
      if (!r.data.places.length) toast.info("Kein Eintrag gefunden – versuche Firmenname + Ort.");
    } catch (e) { toast.error(e.response?.data?.detail || "Suche fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const link = async (placeId) => {
    setBusy(true);
    try {
      await axios.put(`${API}/pages/${pageId}/google`, { place_id: placeId }, { headers: authHeaders() });
      toast.success("Google-Profil verknüpft – Bewertungen erscheinen auf deiner Seite.");
      onChanged(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Verknüpfen fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const unlink = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/pages/${pageId}/google`, { headers: authHeaders() });
      toast.success("Verknüpfung entfernt");
      onChanged(); onClose();
    } catch { toast.error("Entfernen fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="google-reviews-dialog">
        <DialogHeader>
          <DialogTitle>Google-Bewertungen anzeigen</DialogTitle>
          <DialogDescription>Verknüpfe dein Google-Unternehmensprofil. Sterne-Schnitt und bis zu 5 aktuelle Rezensionen erscheinen automatisch auf deiner Seite und werden täglich aktualisiert.</DialogDescription>
        </DialogHeader>
        {linked && (
          <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm" data-testid="google-linked-info">
            <span><strong>{linked.name}</strong> · <Star className="inline w-3.5 h-3.5 text-amber-500" /> {linked.rating} ({linked.count})</span>
            <Button variant="ghost" size="sm" className="text-red-600" onClick={unlink} disabled={busy} data-testid="google-unlink-btn">Entfernen</Button>
          </div>
        )}
        <div className="flex gap-2">
          <Input placeholder="Firmenname und Ort, z. B. Lilium BJJ München" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && q.trim().length >= 3 && search()} data-testid="google-search-input" />
          <Button onClick={search} disabled={busy || q.trim().length < 3} className="bg-indigo-600 hover:bg-indigo-700" data-testid="google-search-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {results.map(p => (
            <button key={p.place_id} onClick={() => link(p.place_id)} disabled={busy} className="w-full text-left border border-slate-200 hover:border-indigo-300 rounded-xl p-3 transition-colors" data-testid={`google-result-${p.place_id}`}>
              <p className="font-semibold text-sm text-slate-900">{p.name}</p>
              <p className="text-xs text-slate-500">{p.address}</p>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
